import { Helmet } from "react-helmet";
import { useState, useEffect } from "react";
import {
  useDeleteRateAlertMutation,
  useGetRateAlertsQuery,
  useCreateRateAlertMutation,
  useUpdateRateAlertMutation,
} from "../../features/account/rateAlertSlice";
import { useCardCategoryQuery } from "../../features/giftCard/cardCategorySlice";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { FiEdit, FiChevronDown } from "react-icons/fi";
import { FiTrash2 } from "react-icons/fi";
import { IoAddSharp, IoClose } from "react-icons/io5";
import toast from "react-hot-toast";

// Types
interface Card {
  _id: string;
  name: string;
  image?: string;
}

interface CardCategory {
  _id: string;
  name: string;
  image?: string;
  cards: Card[];
}

interface RateAlert {
  _id: string;
  rate: number;
  card: Card;
  emailNotification: boolean;
  pushNotification: boolean;
}

export const RateAlerts = () => {
  const [isCreateAlertVisible, setIsCreateAlertVisible] = useState(false);
  const [isUpdateAlertVisible, setIsUpdateAlertVisible] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<RateAlert | null>(null);

  // Card selection states
  const [selectedCategory, setSelectedCategory] = useState<CardCategory | null>(
    null
  );
  const [selectedCard, setSelectedCard] = useState<Card | null>(null);

  // API hooks
  const { data: rateAlerts, isLoading, refetch } = useGetRateAlertsQuery();
  const [deleteRateAlert, { isLoading: isDeleting }] =
    useDeleteRateAlertMutation();

  const { data: categories, isLoading: isCategoriesLoading } =
    useCardCategoryQuery({ page: 1, limit: 100 });

  // Form states for create/update
  const [rateValue, setRateValue] = useState("");
  const [emailNotification, setEmailNotification] = useState(true);
  const [pushNotification, setPushNotification] = useState(true);

  // API mutations
  const [createRateAlert, { isLoading: isCreating }] =
    useCreateRateAlertMutation();
  const [updateRateAlert, { isLoading: isUpdating }] =
    useUpdateRateAlertMutation();

  // Handle delete alert
  const handleDelete = async (id: string) => {
    if (confirm("Are you sure you want to delete this rate alert?")) {
      try {
        toast.loading("Deleting alert...");
        await deleteRateAlert({ id }).unwrap();
        toast.dismiss();
        toast.success("Alert deleted successfully");
        refetch();
      } catch (error) {
        toast.dismiss();
        toast.error("Failed to delete alert");
        console.error(error);
      }
    }
  };

  // Handle category selection
  const handleCategorySelect = (category: CardCategory) => {
    setSelectedCategory(category);
    setSelectedCard(null);
  };

  // Handle card selection
  const handleCardSelect = (card: Card) => {
    setSelectedCard(card);
  };

  // Handle create alert submission
  const handleCreateSubmit = async () => {
    if (!rateValue || !selectedCard) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Validate rate
    const rate = parseFloat(rateValue);
    if (isNaN(rate) || rate <= 0) {
      toast.error("Please enter a valid rate");
      return;
    }

    try {
      toast.loading("Creating alert...");
      const payload = {
        rate,
        card: selectedCard._id,
        emailNotification,
        pushNotification,
      };

      await createRateAlert(payload).unwrap();
      toast.dismiss();
      toast.success("Rate alert created successfully");
      setIsCreateAlertVisible(false);
      resetForm();
      refetch();
    } catch (error) {
      toast.dismiss();
      toast.error("Failed to create rate alert");
      console.error("Failed to create rate alert:", error);
    }
  };

  // Handle update alert submission
  const handleUpdateSubmit = async () => {
    if (!selectedAlert || !rateValue) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Validate rate
    const rate = parseFloat(rateValue);
    if (isNaN(rate) || rate <= 0) {
      toast.error("Please enter a valid rate");
      return;
    }

    try {
      toast.loading("Updating alert...");
      const payload = {
        id: selectedAlert._id,
        rate,
        emailNotification,
        pushNotification,
      };

      await updateRateAlert(payload).unwrap();
      toast.dismiss();
      toast.success("Rate alert updated successfully");
      setIsUpdateAlertVisible(false);
      setSelectedAlert(null);
      refetch();
    } catch (error) {
      toast.dismiss();
      toast.error("Failed to update rate alert");
      console.error("Failed to update rate alert:", error);
    }
  };

  // Reset form values
  const resetForm = () => {
    setRateValue("");
    setSelectedCategory(null);
    setSelectedCard(null);
    setEmailNotification(true);
    setPushNotification(true);
  };

  // Set up update form with selected alert data
  const setupUpdateForm = (alert: RateAlert) => {
    setSelectedAlert(alert);
    setRateValue(alert.rate.toString());
    setEmailNotification(alert.emailNotification);
    setPushNotification(alert.pushNotification);
    setIsUpdateAlertVisible(true);
  };

  // Format number with currency symbol
  const formatCurrency = (amount: number) => {
    return `₦${amount.toLocaleString()}`;
  };

  return (
    <div className="font-dashboard pt-8 pb-12 lg:px-4">
      {/* meta data --- */}
      <Helmet>
        <title>Rate Alerts</title>
        <meta name="description" content="Rate Alerts"></meta>
        <link rel="canonical" href="https://gcbuying.com/app/rate-alerts" />
      </Helmet>
      {/* meta data --- */}

      <div className="container-lg grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-3 min-h-screen">
          <div className="bg-black flex flex-col px-4 lg:px-10 py-6">
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-white text-2xl font-medium">Rate Alerts</h1>
              <button
                onClick={() => setIsCreateAlertVisible(true)}
                className="bg-primary px-4 py-2 rounded-lg text-black flex items-center gap-2"
              >
                <IoAddSharp className="text-xl" />
                <span>Create Alert</span>
              </button>
            </div>

            {/* Alerts Content */}
            {isLoading ? (
              <div className="flex-1 flex justify-center items-center py-20">
                <div className="flex flex-col items-center">
                  <div className="h-10 w-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                  <p className="mt-4 text-white/50">Loading rate alerts...</p>
                </div>
              </div>
            ) : !rateAlerts || rateAlerts.length === 0 ? (
              <div className="flex-1 flex flex-col justify-center items-center py-20">
                <div className="w-32 h-32 rounded-full bg-gray-800 flex items-center justify-center mb-6">
                  <span className="text-5xl text-gray-600">!</span>
                </div>
                <p className="text-white/50 text-lg mb-6">
                  No rate alerts found
                </p>
                <button
                  onClick={() => setIsCreateAlertVisible(true)}
                  className="bg-primary px-6 py-3 rounded-lg text-black font-medium"
                >
                  Create Alert
                </button>
              </div>
            ) : (
              <div className="space-y-3 pb-4">
                {rateAlerts.map((alert: RateAlert) => (
                  <div key={alert._id} className="bg-gray-700 p-4 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="text-white font-medium text-lg">
                        {alert.card?.name || "Rate Alert"}
                      </h3>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setupUpdateForm(alert)}
                          className="p-2 rounded-full hover:bg-gray-600"
                        >
                          <FiEdit className="text-primary" />
                        </button>
                        <button
                          onClick={() => handleDelete(alert._id)}
                          className="p-2 rounded-full hover:bg-gray-600"
                        >
                          <FiTrash2 className="text-red-500" />
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <p className="text-white/60 text-sm">Target:</p>
                      <p className="text-white font-semibold ml-2">
                        {formatCurrency(alert.rate)}
                      </p>
                    </div>

                    <div className="flex mt-3">
                      {alert.emailNotification && (
                        <div className="bg-blue-900/20 px-2 py-1 rounded-full mr-2">
                          <p className="text-blue-400 text-xs">Email</p>
                        </div>
                      )}
                      {alert.pushNotification && (
                        <div className="bg-purple-900/20 px-2 py-1 rounded-full">
                          <p className="text-purple-400 text-xs">Push</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right sidebar - information about rate alerts */}
        <div className="bg-black h-[105vh] px-2 py-4 z-0">
          <div className="flex flex-col h-screen gap-4 max-w-[500px] mx-auto">
            <div className="bg-gray-700 p-6 rounded-lg">
              <h3 className="text-xl text-white mb-4">About Rate Alerts</h3>
              <p className="text-white/70 mb-3">
                Rate alerts notify you when gift card rates reach your target
                value.
              </p>
              <p className="text-white/70 mb-3">
                Set your target rate and choose how you'd like to be notified
                when the rate matches or exceeds your target.
              </p>
              <p className="text-white/70 mb-3">
                <span className="text-primary font-medium">
                  Email notification:
                </span>{" "}
                Get notified via email when your target rate is reached.
              </p>
              <p className="text-white/70 mb-3">
                <span className="text-primary font-medium">
                  Push notification:
                </span>{" "}
                Receive instant push notifications on your device.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Create Alert Modal */}
      <Transition appear show={isCreateAlertVisible} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => setIsCreateAlertVisible(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="font-dashboard w-full max-w-md transform overflow-hidden rounded-2xl bg-[#161819] p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex items-center justify-between pb-4">
                    <div className="flex items-center gap-3">
                      <div className="box-content p-2 rounded-full bg-primary">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M12 6V18"
                            stroke="#343434"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M6 12H18"
                            stroke="#343434"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-medium leading-6 text-white/80"
                      >
                        Create Rate Alert
                      </Dialog.Title>
                    </div>
                    <button
                      onClick={() => setIsCreateAlertVisible(false)}
                      className="text-white/80 hover:text-white"
                    >
                      <IoClose className="text-2xl" />
                    </button>
                  </div>

                  <hr className="border-white/10 mb-6" />

                  <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">
                      Target Rate (NGN)
                    </label>
                    <input
                      type="number"
                      className="form-input"
                      placeholder="Enter target rate"
                      value={rateValue}
                      onChange={(e) => setRateValue(e.target.value)}
                    />
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-white/70 mb-2">
                      Select Category
                    </label>
                    <select
                      className="w-full bg-gray-600 border border-primary/40 rounded-lg text-sm text-white/70 px-4 py-3"
                      value={selectedCategory?._id || ""}
                      onChange={(e) => {
                        const categoryId = e.target.value;
                        const category = categories.data?.find(
                          (c: CardCategory) => c._id === categoryId
                        );
                        if (category) {
                          handleCategorySelect(category);
                        }
                      }}
                    >
                      <option value="" disabled>
                        Select a category
                      </option>
                      {!isCategoriesLoading &&
                        categories &&
                        categories.data?.map((category: CardCategory) => (
                          <option key={category._id} value={category._id}>
                            {category.name}
                          </option>
                        ))}
                    </select>
                  </div>

                  {selectedCategory && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-white/70 mb-2">
                        Select Card
                      </label>
                      <select
                        className="w-full bg-gray-600 border border-primary/40 rounded-lg text-sm text-white/70 px-4 py-3"
                        value={selectedCard?._id || ""}
                        onChange={(e) => {
                          const cardId = e.target.value;
                          const card = selectedCategory?.cards.find(
                            (c: Card) => c._id === cardId
                          );
                          if (card) {
                            handleCardSelect(card);
                          }
                        }}
                      >
                        <option value="" disabled>
                          Select a card
                        </option>
                        {selectedCategory?.cards?.map((card: Card) => (
                          <option key={card._id} value={card._id}>
                            {card.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-white/70 mb-4">
                      Notification Preferences
                    </label>

                    <div className="flex justify-between items-center mb-3 p-2 bg-gray-700 rounded-lg">
                      <span className="text-white/70">Email Notifications</span>
                      <div className="relative inline-block w-12 h-6 transition duration-200 ease-in-out">
                        <input
                          type="checkbox"
                          className="opacity-0 w-12 h-6 absolute z-10 cursor-pointer"
                          checked={emailNotification}
                          onChange={() =>
                            setEmailNotification(!emailNotification)
                          }
                        />
                        <label
                          className={`block overflow-hidden h-6 rounded-full bg-gray-500 cursor-pointer ${
                            emailNotification ? "bg-primary" : ""
                          }`}
                        >
                          <span
                            className={`block h-6 w-6 rounded-full bg-white transform transition-transform duration-200 ease-in-out ${
                              emailNotification
                                ? "translate-x-6"
                                : "translate-x-0"
                            }`}
                          ></span>
                        </label>
                      </div>
                    </div>

                    <div className="flex justify-between items-center p-2 bg-gray-700 rounded-lg">
                      <span className="text-white/70">Push Notifications</span>
                      <div className="relative inline-block w-12 h-6 transition duration-200 ease-in-out">
                        <input
                          type="checkbox"
                          className="opacity-0 w-12 h-6 absolute z-10 cursor-pointer"
                          checked={pushNotification}
                          onChange={() =>
                            setPushNotification(!pushNotification)
                          }
                        />
                        <label
                          className={`block overflow-hidden h-6 rounded-full bg-gray-500 cursor-pointer ${
                            pushNotification ? "bg-primary" : ""
                          }`}
                        >
                          <span
                            className={`block h-6 w-6 rounded-full bg-white transform transition-transform duration-200 ease-in-out ${
                              pushNotification
                                ? "translate-x-6"
                                : "translate-x-0"
                            }`}
                          ></span>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 flex space-x-3">
                    <button
                      type="button"
                      className="w-full inline-flex justify-center rounded-lg border border-transparent bg-gray-600 px-4 py-2 text-sm font-medium text-white hover:bg-gray-700"
                      onClick={() => setIsCreateAlertVisible(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="w-full inline-flex justify-center rounded-lg border border-transparent bg-primary px-4 py-2 text-sm font-medium text-black hover:bg-yellow-500"
                      onClick={handleCreateSubmit}
                      disabled={isCreating}
                    >
                      {isCreating ? (
                        <span className="flex items-center">
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-black"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Creating...
                        </span>
                      ) : (
                        "Create Alert"
                      )}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Update Alert Modal */}
      <Transition appear show={isUpdateAlertVisible} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => setIsUpdateAlertVisible(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="font-dashboard w-full max-w-md transform overflow-hidden rounded-2xl bg-[#161819] p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex items-center justify-between pb-4">
                    <div className="flex items-center gap-3">
                      <div className="box-content p-2 rounded-full bg-primary">
                        <FiEdit size={20} className="text-black" />
                      </div>
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-medium leading-6 text-white/80"
                      >
                        Update Rate Alert
                      </Dialog.Title>
                    </div>
                    <button
                      onClick={() => {
                        setIsUpdateAlertVisible(false);
                        setSelectedAlert(null);
                      }}
                      className="text-white/80 hover:text-white"
                    >
                      <IoClose className="text-2xl" />
                    </button>
                  </div>

                  <hr className="border-white/10 mb-6" />

                  {selectedAlert && (
                    <>
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-white/70 mb-2">
                          Target Rate (NGN)
                        </label>
                        <input
                          type="number"
                          className="form-input"
                          placeholder="Enter target rate"
                          value={rateValue}
                          onChange={(e) => setRateValue(e.target.value)}
                        />
                      </div>

                      <div className="mt-4">
                        <label className="block text-sm font-medium text-white/70 mb-2">
                          Card
                        </label>
                        <div className="bg-gray-600 border border-primary/40 rounded-lg px-4 py-3 text-white/70">
                          {selectedAlert.card?.name || "No card selected"}
                        </div>
                      </div>

                      <div className="mt-4">
                        <label className="block text-sm font-medium text-white/70 mb-4">
                          Notification Preferences
                        </label>

                        <div className="flex justify-between items-center mb-3 p-2 bg-gray-700 rounded-lg">
                          <span className="text-white/70">
                            Email Notifications
                          </span>
                          <div className="relative inline-block w-12 h-6 transition duration-200 ease-in-out">
                            <input
                              type="checkbox"
                              className="opacity-0 w-12 h-6 absolute z-10 cursor-pointer"
                              checked={emailNotification}
                              onChange={() =>
                                setEmailNotification(!emailNotification)
                              }
                            />
                            <label
                              className={`block overflow-hidden h-6 rounded-full bg-gray-500 cursor-pointer ${
                                emailNotification ? "bg-primary" : ""
                              }`}
                            >
                              <span
                                className={`block h-6 w-6 rounded-full bg-white transform transition-transform duration-200 ease-in-out ${
                                  emailNotification
                                    ? "translate-x-6"
                                    : "translate-x-0"
                                }`}
                              ></span>
                            </label>
                          </div>
                        </div>

                        <div className="flex justify-between items-center p-2 bg-gray-700 rounded-lg">
                          <span className="text-white/70">
                            Push Notifications
                          </span>
                          <div className="relative inline-block w-12 h-6 transition duration-200 ease-in-out">
                            <input
                              type="checkbox"
                              className="opacity-0 w-12 h-6 absolute z-10 cursor-pointer"
                              checked={pushNotification}
                              onChange={() =>
                                setPushNotification(!pushNotification)
                              }
                            />
                            <label
                              className={`block overflow-hidden h-6 rounded-full bg-gray-500 cursor-pointer ${
                                pushNotification ? "bg-primary" : ""
                              }`}
                            >
                              <span
                                className={`block h-6 w-6 rounded-full bg-white transform transition-transform duration-200 ease-in-out ${
                                  pushNotification
                                    ? "translate-x-6"
                                    : "translate-x-0"
                                }`}
                              ></span>
                            </label>
                          </div>
                        </div>
                      </div>

                      <div className="mt-8 flex space-x-3">
                        <button
                          type="button"
                          className="w-full inline-flex justify-center rounded-lg border border-transparent bg-gray-600 px-4 py-2 text-sm font-medium text-white hover:bg-gray-700"
                          onClick={() => {
                            setIsUpdateAlertVisible(false);
                            setSelectedAlert(null);
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="w-full inline-flex justify-center rounded-lg border border-transparent bg-primary px-4 py-2 text-sm font-medium text-black hover:bg-yellow-500"
                          onClick={handleUpdateSubmit}
                          disabled={isUpdating}
                        >
                          {isUpdating ? (
                            <span className="flex items-center">
                              <svg
                                className="animate-spin -ml-1 mr-2 h-4 w-4 text-black"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                              </svg>
                              Updating...
                            </span>
                          ) : (
                            "Update Alert"
                          )}
                        </button>
                      </div>
                    </>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

// Types
interface Card {
  _id: string;
  name: string;
  image?: string;
}

interface RateAlert {
  _id: string;
  rate: number;
  card: Card;
  emailNotification: boolean;
  pushNotification: boolean;
}
